import React from 'react';
import {
useMediaQuery, Checkbox, // Button
Tooltip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import usePrevious from 'utils/use-previous';
import { UserContext } from 'contexts/user/user-context';
import { TrainingGroupsContext } from 'routing/routes/config/groups/training/training-groups-context';
import MemberList from 'components/groups/member-list';
import MemberSelector from 'components/groups/member-selector';
import SaveGroupControl from './save-group-control';


const mobileXS = 450;


const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    paddingBottom: theme.spacing(2),
    '& .MuiList-root': {
      marginTop: 0,
    },
    '& .leader, & .mandatory': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    '& .leader': {
      minWidth: 52,
    },
    '& .mandatory': {
      minWidth: 75,
      [theme.breakpoints.down(mobileXS)]: {
        minWidth: 40,
      }
    },
    '& .member-list-header > *': {
      alignSelf: 'flex-end',
      '& .leader, & .mandatory': {
        lineHeight: '16px'
      }
    },
    '& .MuiListItemAvatar-root': {
      [theme.breakpoints.down(mobileXS)]: {
        display: 'none'
      }
    }
  },
  error: {
    marginTop: theme.spacing(1)
  },  
  memberActions: {
    marginTop: theme.spacing(3),
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    margin: theme.spacing(-1),

    '& > *': {
      margin: theme.spacing(1),
    },
  },
}));


// EVENT MEMBERSHIP NOTES:

// HANDLER ROLE
// the creator must lead and attend. this prevents an event from being created and disappearing
// from all record lists. when creating, the creator is obviously the current user. however when
// editing the creator can be a different user.

// TRAINER ROLE
// there must be at least one event member. when creating there are generally no limitations on
// leading or attending. this works because trainers can always see events they created even if
// they aren't a member. however, if a trainer is not the creator then they cannot remove or 
// modify the creator's membership. this prevents a trainer from breaking the handler role rule
// above and potentially causing events to disappear from all lists.
export default function EventMemberControl(props) {
  const {
    translation,
    options,
    storedRecord,
    isGroupUnsynced,    
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { t } = useTranslation(translation);
  const isMobileXS = useMediaQuery(theme => theme.breakpoints.down(mobileXS), { noSsr: true });
  const formContext = useFormContext();
  const { groups } = React.useContext(TrainingGroupsContext);  
  const { control, setValue, formState: { errors } } = formContext;
  const groupId = useWatch({ control, name: 'groupId' });
  const prevGroupId = usePrevious(groupId);
  const members = useWatch({ control, name: 'members' });


  // generate permissions lists & messages
  const [userPermissions, unremovableUserIds] = React.useMemo(() => {
    const permissions = {};
    const unremovableIds = [];

    storedRecord.members.forEach(m => {
      // CAN REMOVE LOGIC
      // - handlers can't remove themselves
      // - handlers can't remove creator
      // - trainers can only remove creator if is self
      // - can't remove any members with completions
      const removeResultReason_Self = (user.role.isHandler && m.id === user.id)
        ? <>You cannot remove yourself</>
        : '';
      const removeResultReason_Creator = ((
        (user.role.isHandler && m.id !== storedRecord.creator.id)
        || (user.role.isTrainer && (m.id !== storedRecord.creator.id || m.id === user.id))
      ) 
        ? ''
        : <>Event creators cannot be removed unless<br />creating from the Trainer role</>);
      const removeResultReason_Completions = !m.hasCompletions
        ? ''
        : <>Members with completions cannot be removed</>;
      const removeResultReason = removeResultReason_Self || removeResultReason_Creator || removeResultReason_Completions;

      // CAN CHANGE LEADERSHIP LOGIC
      // - handlers can't change creator's leadership
      // - trainers can only change creator if is self
      const changeLeaderResultReason_Creator = ((
        (m.id !== storedRecord.creator.id)
        || (user.role.isTrainer && m.id === user.id)
      )
        ? ''
        : <>Event creators must be leaders unless<br />creating from the Trainer role</>);

      // CAN CHANGE ATTENDANCE LOGIC
      // - handlers can't change creator's attendance
      // - trainers can only change creator if is self
      // - can't change any member's attendance with completions
      const changeAttendanceResultReason_Creator = ((
        (m.id !== storedRecord.creator.id)
        || (user.role.isTrainer && m.id === user.id)
      )
        ? ''
        : <>Event creators must attend their event<br />unless creating from the Trainer role</>);
      const changeAttendanceResultReason_Completions = !m.hasCompletions
        ? ''
        : <>Members with completions cannot<br />have their attendance changed</>;
      const changeAttendanceResultReason = changeAttendanceResultReason_Creator || changeAttendanceResultReason_Completions;

      permissions[m.id] = {
        canRemove: !Boolean(removeResultReason),
        removeResultReason: removeResultReason,
        canChangeLeader: !Boolean(changeLeaderResultReason_Creator),
        changeLeaderResultReason: changeLeaderResultReason_Creator,
        canChangeAttendance: !Boolean(changeAttendanceResultReason),
        changeAttendanceResultReason: changeAttendanceResultReason
      };
      
      if (Boolean(removeResultReason)) {
        unremovableIds.push(m.id);
      }
    });

    return [permissions, unremovableIds];
  }, [user, storedRecord]);


  // adjust members when group select is changed
  React.useEffect(() => {
    if (groupId <= 0 || groupId === prevGroupId || prevGroupId === undefined) {
      return;
    }

    const group = groups.find(g => g.id === groupId);

    if (!group) {
      return;
    }

    // keep members that can't be removed, add all other group members
    const newMembers = [
      ...members.filter(m => unremovableUserIds.includes(m.id)),
      ...group.members.filter(m => !unremovableUserIds.includes(m.id))
    ];
    setValue('members', newMembers, { shouldDirty: true });    
  }, [groupId, prevGroupId, groups, members, unremovableUserIds, setValue]);


  const onValidateMembers = (members) => {
    if (members.length === 0) {
      return t('members.members.errMsgEmpty');
    } else {
      return true;
    }
  }


  const onAddMember = (member) => {
    if (isCheckedAllLeader) {
      member.isLeader = true;
    }
    if (isCheckedAllMandatory) {
      member.isOptional = false;
    }
    const newMembers = [...members, member];
    setValue('members', newMembers, { shouldDirty: true, shouldValidate: true });
  }


  const canRemove = (member) => {
    const val = userPermissions[member.id]?.canRemove;
    return val === undefined ? true : val;
  }
  
  
  const removeResultReason = (member) => {
    const reason = userPermissions[member.id]?.removeResultReason;
    return reason || '';
  }


  const canChangeLeader = React.useCallback((member) => {
    const val = userPermissions[member.id]?.canChangeLeader;
    return val === undefined ? true : val;
  }, [userPermissions]);


  const canChangeAttendance = React.useCallback((member) => {
    const val = userPermissions[member.id]?.canChangeAttendance;
    return val === undefined ? true : val;
  }, [userPermissions]);
  
  
  const toggleLeaderAll = (val) => {
    const newMembers = members.map(m => canChangeLeader(m)
      ? { ...m, isLeader: val }
      : m);
    setValue('members', newMembers, { shouldDirty: true });
  }


  const toggleMandatoryAll = (val) => {
    const newMembers = members.map(m => canChangeAttendance(m)
      ? { ...m, isOptional: !val }
      : m);
    setValue('members', newMembers, { shouldDirty: true });
  }


  const leaderCheckbox = (member, props) => {
    const canChange = canChangeLeader(member);
    const canChangeReason = userPermissions[member.id]?.changeLeaderResultReason || '';

    return (
      <Tooltip
        arrow
        placement='top'
        title={canChangeReason}
      >
        <span className='leader'>
          <Checkbox
            color='primary'
            checked={member.isLeader}
            disabled={!canChange}
            onChange={(e, v) => {
              const newMembers = props.value.map(m => m.id === member.id
                ? { ...m, isLeader: v }
                : m);
              props.onChange(newMembers);
            }}
          />
        </span>
      </Tooltip>
    );
  }


  const attendanceCheckbox = (member, props) => {
    const canChange = canChangeAttendance(member);
    const canChangeReason = userPermissions[member.id]?.changeAttendanceResultReason || '';

    return (
      <Tooltip
        arrow
        placement='top'
        title={canChangeReason}
      >
        <span className='mandatory'>
          <Checkbox
            color='primary'
            checked={!member.isOptional}
            disabled={!canChange}
            onChange={(e, v) => {
              const newMembers = props.value.map(m => m.id === member.id
                ? { ...m, isOptional: !v }
                : m);
              props.onChange(newMembers);
            }}
          />
        </span>
      </Tooltip>
    );
  }


  const isCheckedAllLeader = React.useMemo(() => {
    const relevantMembers = members.filter(m => canChangeLeader(m));
    const numChecked = relevantMembers.filter(m => m.isLeader).length;
    return numChecked === relevantMembers.length
      ? true
      : numChecked === 0
        ? false
        : null;
  }, [members, canChangeLeader]);


  const isCheckedAllMandatory = React.useMemo(() => {
    const relevantMembers = members.filter(m => canChangeAttendance(m));
    const numChecked = relevantMembers.filter(m => !m.isOptional).length;
    return numChecked === relevantMembers.length
      ? true
      : numChecked === 0
        ? false
        : null;
  }, [members, canChangeAttendance]);
  

  const headerLabels = [
    'INVITED MEMBERS',

    // LEADER
    <div className='leader'>
      <Tooltip
        arrow
        title={<>A Group Leader can edit the<br />event and add exercises</>}
        placement='top'
        children={<span>Leader</span>}
      />
      <Tooltip
        arrow
        title={<>Select/Unselect all members</>}
        placement='top'
        children={
          <Checkbox
            color="primary"
            style={{ padding: 0 }}
            checked={isCheckedAllLeader === null ? true : isCheckedAllLeader}
            indeterminate={isCheckedAllLeader === null}
            onChange={(e, v) => toggleLeaderAll(isCheckedAllLeader === null ? false : !isCheckedAllLeader)}
          />
        }
      />
    </div>,

    // MANDATORY
    <div className='mandatory'>
      <Tooltip
        arrow
        title={<>Mandatory attendance means the handler must<br />attend. If this is unchecked then the handler<br />may choose whether to attend (opt-in).</>}
        placement='top'
        children={<span>Mand{isMobileXS ? <br/> : ''}atory</span>}
      />
      <Tooltip
        arrow
        title={<>Select/Unselect all members</>}
        placement='top'
        children={
          <span>
            <Checkbox
              color="primary"
              style={{ padding: 0 }}
              checked={isCheckedAllMandatory === null ? true : isCheckedAllMandatory}
              indeterminate={isCheckedAllMandatory === null}
              onChange={(e, v) => toggleMandatoryAll(isCheckedAllMandatory === null ? false : !isCheckedAllMandatory)}
            />
          </span>
        }
      />
    </div>
  ];


  return (
    <>
      <div className={classes.root}>
        <div style={{ maxWidth: '100%' }}>
          <Controller
            control={control}
            name='members'
            rules={{ validate: onValidateMembers }}
            render={({ field }) => (
              <MemberList
                headerLabels={headerLabels}
                members={field.value}
                onChange={field.onChange}
                canRemove={canRemove}
                removeResultReason={removeResultReason}
                renderers={[
                  // LEADER
                  (member) => leaderCheckbox(member, field),
                  // ATTENDANCE
                  (member) => attendanceCheckbox(member, field)
                ]}
              />
            )}
          />

          {errors.members &&
            <Alert severity='error' className={classes.error}>{errors.members.message}</Alert>
          }        

          <div className={`flex-hc ${classes.memberActions}`}>
            <MemberSelector
              options={options.members}
              members={members}
              onAdd={onAddMember}
            />
            <div>
              <SaveGroupControl
                isGroupUnsynced={isGroupUnsynced}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}